import React, { useMemo } from 'react';
import './TagCloud.css';

// Words to exclude from the cloud (stop words + noise)
const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'of', 'in', 'on', 'at', 'to', 'for',
  'with', 'by', 'as', 'is', 'it', 'its', 'this', 'that', 'from', 'into',
  'up', 'but', 'be', 'are', 'was', 'has', 'had', 'not', 'no', 'so', 'if',
  'i', 'me', 'my', 'we', 'you', 'he', 'she', 'they', 'their', 'them',
  'very', 'just', 'style', 'design', 'art', 'like', 'more', 'some', 'all',
  'one', 'two', 'three', 'new', 'color', 'colours', 'colors', 'pattern',
]);

/**
 * Extract top N words from an array of prompt strings.
 * Returns [{word, count}] sorted by count desc.
 */
function extractTopWords(prompts, topN = 20) {
  const freq = {};
  prompts.forEach(prompt => {
    if (!prompt) return;
    // tokenise: lower-case, split on non-alpha chars, min 3 chars
    const tokens = prompt
      .toLowerCase()
      .replace(/[^a-z\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length >= 3 && !STOP_WORDS.has(w));
    tokens.forEach(w => { freq[w] = (freq[w] || 0) + 1; });
  });

  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([word, count]) => ({ word, count }));
}

/**
 * Map a word's rank (0 = highest count) to a tier 1–5.
 */
function getTier(index, total) {
  const ratio = index / Math.max(total - 1, 1); // 0 → top, 1 → bottom
  if (ratio < 0.15) return 5;
  if (ratio < 0.30) return 4;
  if (ratio < 0.50) return 3;
  if (ratio < 0.72) return 2;
  return 1;
}

/**
 * Shuffle an array deterministically (stable across renders) using the
 * word itself as a seed so the layout doesn't jump on re-render.
 */
function pseudoShuffle(arr) {
  return [...arr].sort((a, b) => {
    const ha = a.word.charCodeAt(0) * 37 + a.word.length * 13;
    const hb = b.word.charCodeAt(0) * 37 + b.word.length * 13;
    return ha - hb;
  });
}

/**
 * TagCloud — renders clickable word cloud from community designs.
 *
 * Props:
 *   designs      – array of design objects with .prompt strings
 *   activeWord   – currently active filter word (string | null)
 *   onSelect     – (word | null) => void  called when a word is clicked
 */
function TagCloud({ designs, activeWord, onSelect }) {
  const words = useMemo(() => {
    const prompts = designs.map(d => d.prompt).filter(Boolean);
    const top = extractTopWords(prompts, 20);
    return pseudoShuffle(top);
  }, [designs]);

  if (words.length < 3) return null; // not enough data yet

  return (
    <div className="tag-cloud-section">
      <div className="tag-cloud-label">☁️ Trending words</div>
      <div className="tag-cloud-words">
        {words.map(({ word, count }, index) => {
          const tier = getTier(
            // find original rank (before shuffle) for tier sizing
            words
              .slice()
              .sort((a, b) => b.count - a.count)
              .findIndex(w => w.word === word),
            words.length
          );
          const isActive = activeWord === word;
          return (
            <button
              key={word}
              className={`tag-cloud-word${isActive ? ' active' : ''}`}
              data-tier={tier}
              onClick={() => onSelect(isActive ? null : word)}
              title={`${count} design${count !== 1 ? 's' : ''} use "${word}"`}
            >
              {word}
              <span className="tag-cloud-count">{count}</span>
            </button>
          );
        })}

        {activeWord && (
          <button
            className="tag-cloud-clear"
            onClick={() => onSelect(null)}
            title="Clear tag filter"
          >
            ✕ Clear filter
          </button>
        )}
      </div>
    </div>
  );
}

export default TagCloud;
